import { Children, ReactNode } from "react"

import { useOutlet } from "react-router"

import clsx from "clsx"
import * as m from "motion/react-m"

import { s } from "@/utils/i18n"

import useModal from "@/hooks/useModal"
import useSidebarHidden from "@/hooks/useSidebarHidden"

import Tooltip from "@/components/Tooltip"
import { LabelSmall, P2 } from "@/components/TypeRamp"

import SidebarIcon from "@/icons/Sidebar"

export default function HeaderBar({ children, title }: { title?: ReactNode; children?: ReactNode }) {
  const outlet = useOutlet()
  const hasChildren = Children.count(children) > 0 || !!(outlet as any)?.props?.children?.props?.children
  const { isModalActive } = useModal()

  const [sidebarHidden, setSidebarHidden] = useSidebarHidden()

  return (
    <header
      className="border-empress/20 dark:bg-slate sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b bg-white px-5"
      inert={isModalActive}
    >
      <Tooltip tip={s`Show sidebar`} side="right">
        <m.button
          animate={{
            width: !sidebarHidden ? 0 : "calc(var(--spacing) * 7)",
            paddingInline: !sidebarHidden ? 0 : "var(--spacing)",
            marginLeft: !sidebarHidden ? "calc(var(--spacing) * -2)" : "calc(var(--spacing) * -1)",
          }}
          initial={{ width: 0, paddingInline: 0, marginLeft: "calc(var(--spacing) * -2)" }}
          type="button"
          className="cursor-pointer overflow-hidden rounded-md py-1 transition-colors hover:bg-black/5"
          onClick={() => setSidebarHidden((b) => !b)}
          tabIndex={sidebarHidden ? undefined : -1}
          aria-hidden={!sidebarHidden}
        >
          <SidebarIcon className="h-5 w-5" />
          <span className="sr-only">{s`Show sidebar`}</span>
        </m.button>
      </Tooltip>
      {title && (
        <P2 as="div" className={clsx(hasChildren && "dark:text-swiss text-empress", "transition")}>
          {title}
        </P2>
      )}
      {title && hasChildren && (
        <LabelSmall as="span" className="opacity-20" aria-hidden="true">
          /
        </LabelSmall>
      )}
      {children || outlet}
    </header>
  )
}
